/**
 * Knowledgebase Hub API (services/agent/app/kb_routes.py): search over the team SOPs
 * indexed from DWA's Google shared drive. DWA users only; `syncKnowledgebase` and the
 * sources calls (Admin → Knowledgebase sources) are dwa_admin only. KB knowledgebase.md.
 */
import { authService, authFetch } from './auth';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';

export type KbType = 'doc' | 'sheet' | 'slides' | 'pdf' | 'text' | 'other';

export const KB_TYPE_LABELS: Record<KbType, string> = {
  doc: 'Docs',
  sheet: 'Sheets',
  slides: 'Slides',
  pdf: 'PDFs',
  text: 'Text',
  other: 'Other files',
};

export type KbSort = 'relevance' | 'modified' | 'title' | 'folder' | 'type';
export type KbOrder = 'asc' | 'desc';

export interface KbSnippetSegment {
  text: string;
  match: boolean;
}

export interface KbResult {
  file_id: string;
  title: string;
  folder_path: string;
  mime_type: string | null;
  kind: KbType;
  kind_label: string;
  web_view_link: string | null;
  modified_time: string | null;
  last_modified_by: string | null;
  text_indexed: boolean;
  rank: number | null;
  snippet: KbSnippetSegment[];
}

export interface KbSearchResponse {
  query: string;
  mode: 'search' | 'browse';
  folder: string | null;
  type: KbType | null;
  sort?: KbSort;
  order?: KbOrder;
  total: number;
  limit: number;
  offset: number;
  page?: number;
  page_size?: number;
  results: KbResult[];
}

/** One document with its extracted text (the viewer's Text tab). */
export interface KbDocument {
  file_id: string;
  title: string;
  folder_path: string;
  mime_type: string | null;
  kind: KbType;
  kind_label: string;
  web_view_link: string | null;
  modified_time: string | null;
  last_modified_by: string | null;
  extract_status: 'text' | 'metadata_only' | 'error' | null;
  text_truncated: boolean;
  /** 'markdown' for Google Docs (exported as markdown), else 'text'. */
  text_format: 'markdown' | 'text';
  text: string;
}

export interface KbFolder {
  path: string;
  name: string;
  depth: number;
  /** Documents in this folder and its subfolders. */
  count: number;
  direct: number;
}

export interface KbFoldersResponse {
  folders: KbFolder[];
  root_count: number;
}

export interface KbSyncRun {
  status: 'running' | 'success' | 'partial' | 'failed' | 'skipped';
  started_at: string;
  finished_at: string | null;
  counts: Record<string, number>;
  details: { reason?: string; missing?: string[]; [k: string]: unknown };
}

export interface KbStatusResponse {
  source: string;
  document_count: number;
  newest_modified: string | null;
  latest_run: KbSyncRun | null;
  last_success: KbSyncRun | null;
}

/** Error with the API's `detail` (a string, or `{message, invalid}` for folder validation). */
export class KbApiError extends Error {
  status: number;
  invalid: Record<string, string> | null;

  constructor(message: string, status: number, invalid: Record<string, string> | null = null) {
    super(message);
    this.status = status;
    this.invalid = invalid;
  }
}

async function request<T>(method: 'GET' | 'POST' | 'PUT' | 'PATCH', path: string, body?: unknown): Promise<T> {
  const token = authService.getAccessToken();
  const resp = await authFetch(`${API_BASE}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    let invalid: Record<string, string> | null = null;
    try {
      const payload = await resp.json();
      if (typeof payload.detail === 'string') detail = payload.detail;
      else if (payload.detail && typeof payload.detail === 'object') {
        detail = payload.detail.message || detail;
        invalid = payload.detail.invalid ?? null;
      }
    } catch {
      /* ignore */
    }
    throw new KbApiError(detail, resp.status, invalid);
  }
  return resp.json() as Promise<T>;
}

export function searchKnowledgebase(params: {
  q?: string;
  folder?: string | null;
  type?: KbType | null;
  limit?: number;
  offset?: number;
  sort?: KbSort | null;
  order?: KbOrder | null;
  page?: number;
  pageSize?: number;
}): Promise<KbSearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set('q', params.q);
  if (params.folder) qs.set('folder', params.folder);
  if (params.type) qs.set('type', params.type);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  if (params.sort) qs.set('sort', params.sort);
  if (params.order) qs.set('order', params.order);
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('page_size', String(params.pageSize));
  const query = qs.toString();
  return request<KbSearchResponse>('GET', `/kb/search${query ? `?${query}` : ''}`);
}

export function fetchKbFolders(): Promise<KbFoldersResponse> {
  return request<KbFoldersResponse>('GET', '/kb/folders');
}

export function fetchKbStatus(): Promise<KbStatusResponse> {
  return request<KbStatusResponse>('GET', '/kb/status');
}

export function syncKnowledgebase(): Promise<{ queued: boolean; task_id: string }> {
  return request('POST', '/kb/sync');
}

const NATIVE_PREVIEW: Record<string, string> = {
  'application/vnd.google-apps.document': 'https://docs.google.com/document/d/',
  'application/vnd.google-apps.spreadsheet': 'https://docs.google.com/spreadsheets/d/',
  'application/vnd.google-apps.presentation': 'https://docs.google.com/presentation/d/',
};

/**
 * Google's embeddable preview for a file. Stored files (PDF, Word, images…) use
 * drive.google.com/file/d/<id>/preview; Google Docs/Sheets/Slides use their own
 * editor host's /preview, the documented embed for native files. Either only
 * renders for a viewer signed in to Google with access to the shared drive.
 */
export function drivePreviewUrl(fileId: string, mimeType: string | null | undefined): string {
  const id = encodeURIComponent(fileId);
  const native = mimeType ? NATIVE_PREVIEW[mimeType] : undefined;
  return native ? `${native}${id}/preview` : `https://drive.google.com/file/d/${id}/preview`;
}

export function fetchKbDocument(fileId: string): Promise<KbDocument> {
  return request<KbDocument>('GET', `/kb/documents/${encodeURIComponent(fileId)}`);
}

// ─── Sources (dwa_admin) ─────────────────────────────────────────────────────

export interface KbSourceFolder {
  folder_id: string;
  name: string | null;
  path: string | null;
  include_subfolders: boolean;
  added_by: string | null;
  added_at: string | null;
  /** Documents indexed now under the folder (null until its path is known). */
  document_count: number | null;
  /** Files the last successful sync listed there. */
  last_sync_files: number | null;
  last_sync_error: string | null;
}

export interface KbSource {
  id: number;
  source_key: string;
  kind: string;
  drive_id: string;
  display_name: string;
  audience: string;
  enabled: boolean;
  index_whole_drive: boolean;
  created_by: string | null;
  created_at: string | null;
  updated_by: string | null;
  updated_at: string | null;
  document_count: number;
  latest_run: KbSyncRun | null;
  last_success: KbSyncRun | null;
  folders: KbSourceFolder[];
}

export interface KbBrowseFolder {
  id: string;
  name: string;
  selected: boolean;
  inherited: boolean;
}

export interface KbBrowseResponse {
  source_id: number;
  parent: string;
  path: { id: string; name: string }[];
  parent_selected: boolean;
  parent_inherited: boolean;
  folders: KbBrowseFolder[];
  next_page_token: string | null;
}

export function fetchKbSources(): Promise<{ sources: KbSource[] }> {
  return request('GET', '/kb/sources');
}

export function browseKbSource(sourceId: number, parent = 'root', pageToken?: string | null): Promise<KbBrowseResponse> {
  const qs = new URLSearchParams({ parent });
  if (pageToken) qs.set('page_token', pageToken);
  return request('GET', `/kb/sources/${sourceId}/browse?${qs.toString()}`);
}

export function saveKbSourceFolders(
  sourceId: number,
  folderIds: string[],
): Promise<{ source: KbSource; added: string[]; removed: string[] }> {
  return request('PUT', `/kb/sources/${sourceId}/folders`, { folder_ids: folderIds });
}

export function updateKbSource(
  sourceId: number,
  changes: Partial<Pick<KbSource, 'display_name' | 'enabled' | 'index_whole_drive'>>,
): Promise<{ source: KbSource }> {
  return request('PATCH', `/kb/sources/${sourceId}`, changes);
}

// ─── Omega Wolf Guide (kb_routes /kb/guide*, guide.py) ───────────────────────

export interface GuideTocPage {
  slug: string;
  title: string;
  summary: string;
}

export interface GuideToc {
  sections: { name: string; pages: GuideTocPage[] }[];
}

export interface GuideHit extends GuideTocPage {
  section: string;
  snippet: string;
}

export interface GuidePage {
  slug: string;
  title: string;
  section: string;
  summary: string;
  body: string;
  /** The page has a technical layer (shown only with the Technical permission). */
  has_technical: boolean;
  /** Developer Knowledge Bank files; null unless the viewer holds Technical. */
  technical: { file: string; title: string; body: string }[] | null;
  prev: string | null;
  next: string | null;
}

export function fetchGuideToc(): Promise<GuideToc> {
  return request<GuideToc>('GET', '/kb/guide');
}

export function searchGuide(q: string): Promise<{ query: string; results: GuideHit[] }> {
  return request<{ query: string; results: GuideHit[] }>('GET', `/kb/guide/search?q=${encodeURIComponent(q)}`);
}

export function fetchGuidePage(slug: string): Promise<GuidePage> {
  return request<GuidePage>('GET', `/kb/guide/${encodeURIComponent(slug)}`);
}

// ─── Internal forms (JotForm "Omega Wolf" label, kb_routes /kb/forms) ────────

export interface InternalForm {
  id: string;
  title: string;
  workspace: string;
  url: string;
  updated_at: string | null;
  /** Short description (app/docs/guide/_internal-forms.json). */
  description?: string | null;
  /** Where a submission lands in Teamwork, once recorded. */
  teamwork?: { project: string | null; task_list: string | null } | null;
  /** A web page the form is also embedded on (optional). */
  page_url?: string | null;
}

export function fetchInternalForms(): Promise<{ forms: InternalForm[] }> {
  return request<{ forms: InternalForm[] }>('GET', '/kb/forms');
}
